import { useCallback, useEffect, useState } from "react"
import PaginationWrapper from "../wrapper"
import If from "../../if"

interface Props<T extends any[]> {
  className?: string
  items: T
  itemsPerPage?: number
  initialPage?: number
  updateDisplayedItems: (items: T) => void
}

const CustomPagination = <T extends any[]>({
  className = "",
  items,
  itemsPerPage = 10,
  initialPage = 0,
  updateDisplayedItems
}: Props<T>) => {
  const [currentPage, setCurrentPage] = useState(initialPage)

  // Pagination Controls
  const totalPages = Math.ceil(items.length / itemsPerPage)

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }, [currentPage, totalPages])

  const handlePrevPage = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1)
    }
  }, [currentPage])

  // Pagination Effect: Update the current page orgs based on page changes
  useEffect(() => {
    const startIndex = currentPage * itemsPerPage
    const endIndex = startIndex + itemsPerPage

    updateDisplayedItems(items.slice(startIndex, endIndex) as T)
  }, [currentPage, items, itemsPerPage, updateDisplayedItems])

  return (
    <If
      condition={totalPages > 1}
      then={
        <PaginationWrapper
          className={className}
          pageIndex={currentPage}
          pageSize={itemsPerPage}
          disabledPrev={currentPage === 0}
          disabledNext={currentPage === totalPages - 1}
          onClickPrev={handlePrevPage}
          onClickNext={handleNextPage}
          rowCount={items.length}
          setPageIndex={setCurrentPage}
        />
      }
    />
  )
}

export default CustomPagination
